import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { Observable } from 'rxjs';
import { environment } from '../../../environments/environment';
import { ConsentEvidenceForm, FrequencyCapForm, PreferredChannelForm, QuietHoursForm, TemplateVariableForm } from './growth-comms.models';

export interface ConsentPreferenceResponse {
  id: string;
  tenantId: string;
  partyId: string;
  purposeKey: string;
  channelKey: string;
  state: string;
  legalBasis: string | null;
  evidence: ConsentEvidenceForm;
  source: string;
  effectiveAtUtc: string;
  expiresAtUtc: string | null;
  correlationId: string;
}

export interface PreferenceCenterResponse {
  id: string;
  tenantId: string;
  partyId: string;
  locale: string;
  preferredChannels: PreferredChannelForm[];
  quietHours: QuietHoursForm;
  frequencyCaps: FrequencyCapForm[];
  isEnabled: boolean;
}

export interface InteractionResponse {
  id: string;
  tenantId: string;
  partyId: string | null;
  leadId: string | null;
  campaignId: string | null;
  contentAssetId: string | null;
  channelKey: string;
  direction: string;
  purposeKey: string;
  status: string;
  externalMessageId: string | null;
  providerKey: string | null;
  payloadJson: string;
  occurredAtUtc: string;
  correlationId: string;
  idempotencyKey: string;
}

export interface SendMessageResponse {
  interactionId: string | null;
  status: string;
  externalMessageId: string | null;
  providerKey: string | null;
  isSandbox: boolean;
  isIdempotentReplay: boolean;
  blockReason: string | null;
}

export interface DeliverabilitySummaryResponse {
  channelKey: string;
  purposeKey: string;
  status: string;
  count: number;
}

export interface MessageTemplateResponse {
  id: string;
  tenantId: string;
  templateKey: string;
  channelKey: string;
  purposeKey: string;
  subjectTemplate: string | null;
  bodyTemplate: string;
  status: string;
  variables: TemplateVariableForm[];
}

@Injectable({ providedIn: 'root' })
export class GrowthCommsService {
  private readonly http = inject(HttpClient);
  private readonly base = `${environment.apiBaseUrl}/api/v1/growth/comms`;

  getConsent(partyId: string): Observable<ConsentPreferenceResponse[]> {
    return this.http.get<ConsentPreferenceResponse[]>(`${this.base}/consent/${partyId}`);
  }

  updateConsent(partyId: string, items: Array<{
    purposeKey: string;
    channelKey: string;
    state: string;
    legalBasis?: string | null;
    evidence?: ConsentEvidenceForm | null;
    source?: string | null;
  }>): Observable<ConsentPreferenceResponse[]> {
    return this.http.put<ConsentPreferenceResponse[]>(`${this.base}/consent/${partyId}`, { items });
  }

  getPreferenceCenter(partyId: string): Observable<PreferenceCenterResponse> {
    return this.http.get<PreferenceCenterResponse>(`${this.base}/preference-center/${partyId}`);
  }

  updatePreferenceCenter(partyId: string, payload: {
    locale?: string;
    preferredChannels?: PreferredChannelForm[];
    quietHours?: QuietHoursForm;
    frequencyCaps?: FrequencyCapForm[];
    isEnabled?: boolean;
  }): Observable<PreferenceCenterResponse> {
    return this.http.put<PreferenceCenterResponse>(`${this.base}/preference-center/${partyId}`, {
      locale: payload.locale,
      preferredChannels: payload.preferredChannels?.map(item => ({ channelKey: item.channelKey, priority: item.priority })),
      quietHours: payload.quietHours,
      frequencyCaps: payload.frequencyCaps?.map(item => ({
        purposeKey: item.purposeKey,
        channelKey: item.channelKey,
        maxPerDay: item.maxPerDay,
        maxPerWeek: item.maxPerWeek
      })),
      isEnabled: payload.isEnabled
    });
  }

  listInteractions(filters: { partyId?: string; leadId?: string; channel?: string; status?: string }): Observable<InteractionResponse[]> {
    let params = new HttpParams();
    if (filters.partyId) params = params.set('partyId', filters.partyId);
    if (filters.leadId) params = params.set('leadId', filters.leadId);
    if (filters.channel) params = params.set('channel', filters.channel);
    if (filters.status) params = params.set('status', filters.status);
    return this.http.get<InteractionResponse[]>(`${this.base}/interactions`, { params });
  }

  getDeliverability(filters: { channel?: string; purpose?: string }): Observable<DeliverabilitySummaryResponse[]> {
    let params = new HttpParams();
    if (filters.channel) params = params.set('channel', filters.channel);
    if (filters.purpose) params = params.set('purpose', filters.purpose);
    return this.http.get<DeliverabilitySummaryResponse[]>(`${this.base}/deliverability`, { params });
  }

  listTemplates(): Observable<MessageTemplateResponse[]> {
    return this.http.get<MessageTemplateResponse[]>(`${this.base}/templates`);
  }

  createTemplate(payload: {
    templateKey: string;
    channelKey: string;
    purposeKey: string;
    subjectTemplate?: string | null;
    bodyTemplate: string;
    status?: string;
    variables?: TemplateVariableForm[];
  }): Observable<MessageTemplateResponse> {
    return this.http.post<MessageTemplateResponse>(`${this.base}/templates`, {
      ...payload,
      variables: payload.variables?.map(item => ({
        key: item.key,
        label: item.label,
        required: item.required,
        defaultValue: item.defaultValue
      }))
    });
  }

  updateTemplate(id: string, payload: {
    subjectTemplate?: string | null;
    bodyTemplate: string;
    status?: string;
    variables?: TemplateVariableForm[];
  }): Observable<MessageTemplateResponse> {
    return this.http.put<MessageTemplateResponse>(`${this.base}/templates/${id}`, {
      ...payload,
      variables: payload.variables?.map(item => ({
        key: item.key,
        label: item.label,
        required: item.required,
        defaultValue: item.defaultValue
      }))
    });
  }

  sendMessage(payload: {
    partyId: string;
    channelKey: string;
    purposeKey: string;
    subject?: string | null;
    body: string;
    destinationIdentifier?: string | null;
    idempotencyKey?: string;
  }): Observable<SendMessageResponse> {
    return this.http.post<SendMessageResponse>(`${this.base}/send`, payload);
  }
}
